import React, { ReactElement, Reducer } from 'react'
import Retriever, { RetrieverConfig } from '../common/Retriever'
import PersonLink from '../Person/PersonLink'
import PersonContent from '../Person/PersonContent'
import PersonThumbnail from '../Person/PersonThumbnail'
import { treatLocalAction, treatApiAction } from '../helpers/context'
import { actionKey } from '../helpers/action/action'
import { PersonContext, contextConfig } from '../contexts/PersonContext'
import { PersonContextState } from '../contexts/PersonContext.d'
import { AnyAction, ApiResponseAction, PrimaryAction } from '../helpers/action/Action.d'
import { PersonAction } from '../helpers/action/PersonAction.d'
import { SuperDispatch } from '../helpers/dispatch.d'
import { formatDate } from '../helpers/format'
import Graph from '../common/Graph'

const GRAPH_COUNTRIES_MAX = 12

export const PersonReducer: Reducer<PersonContextState, AnyAction> = (state, action) => {
    return 'apiResponse' in action
        ? PersonReducerApiResponse(state, action)
        : PersonReducerLocal(state, action)
}

export const PersonReducerApiResponse: Reducer<PersonContextState, ApiResponseAction> = (state, action) => {
    const newState = treatLocalAction(state, action, contextConfig)
    // Clear any graphs of the same type which are not currently being shown, to avoid cluttering up state.
    if (action.type.startsWith('GRAPH_') && newState.graphs) {
        const currentKey = actionKey(action)
        return { ...newState, graphs: Object.fromEntries(
            Object.entries(newState.graphs).filter(([key]) =>
                key === currentKey || JSON.parse(key)?.type !== action.type
            )
        ) }
    }
    return newState
}

// This function governs actions which update local state.
export const PersonReducerLocal: Reducer<PersonContextState, PrimaryAction> = (state, action) => {
    switch (action.type) {
        case 'SELECT_PERSON':
            return { ...state, selectedPerson: Number(action.personId) }

        case 'SET_COMPARED_PEOPLE':
            return { ...state, comparedPeople: action.people }

        default:
            return treatLocalAction(state, action, contextConfig)
    }
}

// This function governs API-calling actions. They may later end up above.
export const PersonDispatcher: SuperDispatch<PersonAction> = (action, dispatch, dispatchToApi) => {
    switch (action.type) {
        case 'GRAPH_PEOPLE_OVER_TIME':
            return dispatchToApi(`graph/person?selection=${action.people.join(',')}&rawData=true`)

        case 'GRAPH_WORDS_CLOUD_BETWEEN_DATES':
            return dispatchToApi(`graph/cloud/between/${
                formatDate(action.start * 1000)}/${formatDate(action.end ? action.end * 1000 : new Date())
            }?type=word&rawData=true`)

        case 'GRAPH_PEOPLE_CLOUD_BETWEEN_DATES':
            return dispatchToApi(`graph/cloud/between/${
                formatDate(action.start * 1000)}/${formatDate(action.end ? action.end * 1000 : new Date())
            }?rawData=true`)

        case 'GRAPH_PEOPLE_LINE_BETWEEN_DATES':
            return dispatchToApi(`graph/people/between/${
                formatDate(action.start * 1000)}/${formatDate(action.end ? action.end * 1000 : new Date())
            }?rawData=true`)

        case 'GRAPH_PEOPLE_FOR_PERSON':
            return dispatchToApi(`graph/cloud?person=${action.person}&rawData=true`)

        case 'GRAPH_COUNTRIES_BAR':
            return dispatchToApi(`graph/countries?rawData=true&max=${GRAPH_COUNTRIES_MAX}`)

        default:
            return treatApiAction(action, dispatch, dispatchToApi, contextConfig)
    }
}

// This function governs loading of data: see common/Retriever.js for details.
const PersonRetrieverConfig: RetrieverConfig<PersonContextState, PrimaryAction> = (dataType) => {
    switch (dataType) {
        case PersonContent:
        case PersonLink:
        case PersonThumbnail:
            return {
                isDataPresent: (state, props: { personId?: number | string }) =>
                    !!state.people && !!props.personId && props.personId in state.people,
                actionToDispatch: (_state, props) => ({ type: 'GET_PERSON', id: Number(props.personId) })
            }
        case Graph:
            return {
                isDataPresent: (state, props: { action?: PrimaryAction }) =>
                    !!state.graphs && !!props.action && JSON.stringify(props.action) in state.graphs,
                actionToDispatch: (_state, props: { action?: PrimaryAction }) => props.action,
                loadingMessage: (_, action) => {
                    if (action.type.includes('CLOUD')) {
                        return 'Drawing wordcloud...'
                    } else {
                        return 'Drawing graph...'
                    }
                }
            }
        default:
            return undefined
    }
}

export const PersonRetriever = (props: { children: ReactElement, inactive?: boolean }): ReactElement =>
    <Retriever context={ PersonContext } messageOn404="Nobody could be found" config={ PersonRetrieverConfig } inactive={ props.inactive }>
        {props.children}
    </Retriever>
